import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const ContactPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('sending');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);

    try {
      const res = await fetch('https://readdy.ai/api/form/d77a2okggtbbtt19rgm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const contactInfo = [
    { icon: 'ri-phone-line', labelKey: 'contact.info_phone', value: '+1 754-203-4748', href: 'tel:+17542034748' },
    { icon: 'ri-map-pin-line', labelKey: 'contact.info_location', value: 'Indianapolis, IN 46224', href: null },
    { icon: 'ri-whatsapp-line', labelKey: 'contact.info_whatsapp', value: 'wa.me/17542034748', href: 'https://wa.me/17542034748' },
  ];

  return (
    <div className="min-h-screen bg-white font-sans pt-20">

      {/* Hero */}
      <section className="relative min-h-[380px] md:min-h-[440px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/ac3ed5b188e62e6a9ccfa067496f36cd.jpg"
          alt="Contacto Printven LLC"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/80 via-[#0A1628]/65 to-[#0A1628]/80"></div>
        <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
          <span className="inline-block bg-[#FF6B35]/20 text-[#FF6B35] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 border border-[#FF6B35]/30">
            {t('contact.badge')}
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-4">
            {t('contact.hero_title')}
          </h1>
          <p className="text-white/70 text-base md:text-lg">
            {t('contact.hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">

          {/* Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <h2 className="text-2xl font-extrabold text-[#0A1628] mb-2">{t('contact.info_title')}</h2>
              <p className="text-gray-500 text-sm leading-relaxed">{t('contact.info_desc')}</p>
            </div>

            <div className="flex flex-col gap-4">
              {contactInfo.map((item) => (
                <div key={item.labelKey} className="flex items-start gap-4 bg-gray-50 rounded-2xl p-5">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-[#FF6B35] text-xl`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">{t(item.labelKey)}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith('https') ? '_blank' : undefined}
                        rel={item.href.startsWith('https') ? 'nofollow noreferrer' : undefined}
                        className="text-[#0A1628] font-semibold text-sm hover:text-[#FF6B35] transition-colors cursor-pointer"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-[#0A1628] font-semibold text-sm">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <div className="bg-[#0A1628] rounded-2xl p-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#25D366]/20 rounded-2xl mx-auto mb-4">
                <i className="ri-whatsapp-line text-[#25D366] text-3xl"></i>
              </div>
              <h3 className="text-white font-bold text-base mb-2">{t('contact.wa_title')}</h3>
              <p className="text-white/55 text-sm mb-4">{t('contact.wa_desc')}</p>
              <a
                href="https://wa.me/17542034748"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-2.5 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#1ebe5a] transition-colors text-sm"
              >
                <i className="ri-whatsapp-line text-base"></i>
                {t('cta.whatsapp')}
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-100 rounded-3xl p-8 md:p-10">
              <h2 className="text-2xl font-extrabold text-[#0A1628] mb-2">{t('contact.form_title')}</h2>
              <p className="text-gray-500 text-sm mb-8">{t('contact.form_desc')}</p>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-5">
                    <i className="ri-check-line text-green-500 text-3xl"></i>
                  </div>
                  <h3 className="text-[#0A1628] font-bold text-xl mb-2">{t('contact.success_title')}</h3>
                  <p className="text-gray-500 text-sm max-w-xs">{t('contact.success_desc')}</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 bg-[#FF6B35] text-white font-semibold px-6 py-2.5 rounded-full text-sm cursor-pointer hover:bg-[#e85a24] transition-colors whitespace-nowrap"
                  >
                    {t('contact.send_another')}
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-form-printven"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        {t('contact.field_name')} <span className="text-[#FF6B35]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder={t('contact.placeholder_name')}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#FF6B35] transition-colors bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        {t('contact.field_email')} <span className="text-[#FF6B35]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder={t('contact.placeholder_email')}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#FF6B35] transition-colors bg-gray-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      {t('contact.field_phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={t('contact.placeholder_phone')}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#FF6B35] transition-colors bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      {t('contact.field_service')}
                    </label>
                    <select
                      name="service"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#FF6B35] transition-colors bg-gray-50 cursor-pointer"
                    >
                      <option value="">{t('contact.select_service')}</option>
                      <option value="DTF">Impresión DTF</option>
                      <option value="Sublimación">Sublimación</option>
                      <option value="Impresión Comercial">Impresión Comercial</option>
                      <option value="Microperforados">Microperforados</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      {t('contact.field_message')} <span className="text-[#FF6B35]">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder={t('contact.placeholder_message')}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#FF6B35] transition-colors bg-gray-50 resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      <i className="ri-error-warning-line text-red-400 text-lg"></i>
                      <p className="text-red-500 text-sm">{t('contact.error_msg')}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="w-full bg-[#FF6B35] text-white font-bold py-3.5 rounded-xl text-sm cursor-pointer hover:bg-[#e85a24] transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        {t('contact.sending')}
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-base"></i>
                        {t('contact.submit')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden border border-gray-100 h-72">
            <iframe
              title="Printven LLC - Indianapolis, IN"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3081.0!2d-86.2520!3d39.7684!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sIndianapolis%2C+IN+46224!5e0!3m2!1sen!2sus!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;
